import { doc, updateDoc, collection, query, where, getDocs, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { User } from '../types/index';

const WHITELISTED_DOMAINS = ['redmaxevents.com'];

export class UserService {
  /**
   * Check if an email is automatically approved (whitelisted domain)
   */
  static isEmailWhitelisted(email: string): boolean {
    const domain = email.split('@')[1]?.toLowerCase();
    return WHITELISTED_DOMAINS.includes(domain || '');
  }

  /**
   * Approve a user (admin only)
   */
  static async approveUser(userId: string, approverUid: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isApproved: true,
        isActive: true,  // Ensure user is active when approved
        emailVerified: true,  // Set email as verified when admin approves
        status: 'approved',
        approvedAt: Timestamp.now(),
        approvedBy: approverUid,
      });
    } catch (error: any) {
      throw new Error(`Failed to approve user: ${error.message}`);
    }
  }

  /**
   * Reject a user (mark as rejected, not approved)
   */
  static async rejectUser(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isApproved: false,
        status: 'rejected',
        rejectedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to reject user: ${error.message}`);
    }
  }

  /**
   * Deactivate a user (approved but disabled)
   */
  static async deactivateUser(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: false,
        status: 'deactivated',
        deactivatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to deactivate user: ${error.message}`);
    }
  }

  /**
   * Reactivate a user
   */
  static async reactivateUser(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: true,
        isApproved: true,  // Ensure approved
        emailVerified: true,  // Ensure email verified
        status: 'approved',
        deactivatedAt: null,
      });
    } catch (error: any) {
      throw new Error(`Failed to reactivate user: ${error.message}`);
    }
  }

  /**
   * Get all deactivated users
   */
  static async getDeactivatedUsers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, 'users'),
        where('status', '==', 'deactivated')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
          approvedAt: data.approvedAt ? new Date(data.approvedAt) : undefined,
        } as User;
      });
    } catch (error: any) {
      throw new Error(`Failed to fetch deactivated users: ${error.message}`);
    }
  }

  /**
   * Delete a user (mark as deleted - keeps Firestore record to avoid Firebase Auth orphaning)
   * Note: Firestore record is kept to maintain referential integrity with Firebase Auth
   */
  static async deleteUser(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: false,
        isApproved: false,
        status: 'deleted',
        deletedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to delete user: ${error.message}`);
    }
  }

  /**
   * Get all pending users (awaiting approval)
   */
  static async getPendingUsers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, 'users'),
        where('status', '==', 'pending')
      );
      const querySnapshot = await getDocs(q);
      // Filter out any users that have rejectedAt timestamp (for backwards compatibility)
      return querySnapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            ...data,
            createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
            approvedAt: data.approvedAt ? new Date(data.approvedAt) : undefined,
          } as User;
        })
        .filter((user) => !user.rejectedAt);
    } catch (error: any) {
      throw new Error(`Failed to fetch pending users: ${error.message}`);
    }
  }

  /**
   * Get all rejected users
   */
  static async getRejectedUsers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, 'users'),
        where('status', '==', 'rejected')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
          rejectedAt: data.rejectedAt ? new Date(data.rejectedAt) : undefined,
        } as User;
      });
    } catch (error: any) {
      throw new Error(`Failed to fetch rejected users: ${error.message}`);
    }
  }

  /**
   * Get all approved users
   */
  static async getApprovedUsers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, 'users'),
        where('status', '==', 'approved')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
          approvedAt: data.approvedAt ? new Date(data.approvedAt) : undefined,
        } as User;
      });
    } catch (error: any) {
      throw new Error(`Failed to fetch approved users: ${error.message}`);
    }
  }

  /**
   * Get all users
   */
  static async getAllUsers(): Promise<User[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
          approvedAt: data.approvedAt ? new Date(data.approvedAt) : undefined,
        } as User;
      });
    } catch (error: any) {
      throw new Error(`Failed to fetch users: ${error.message}`);
    }
  }

  /**
   * Update user role
   */
  static async updateUserRole(
    userId: string,
    role: 'admin' | 'sales' | 'viewer'
  ): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), { role });
    } catch (error: any) {
      throw new Error(`Failed to update user role: ${error.message}`);
    }
  }
}
